import React, { useState, useEffect, useContext } from 'react';
import { Container, Badge, ProgressBar, Form, Row, Col, InputGroup, Button, Dropdown, OverlayTrigger, Popover } from 'react-bootstrap';
import { AuthContext } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import TasksService from '../../../services/TasksService';
import { FaCheckCircle, FaTimesCircle, FaBan, FaSearch, FaFilter, FaSort, FaSortUp, FaSortDown, FaTimes } from 'react-icons/fa';
import Spinner from '../../../components/Spinner';
import ProfileImage from '../../../components/ProfileImage';

const TaskList = ({ panels = [], sectionVisibility = {}, onSectionVisibilityChange, darkMode = false }) => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [taskTypeFilter, setTaskTypeFilter] = useState('');
    const [sortBy, setSortBy] = useState('updatedAt'); // 'updatedAt', 'dueDate'
    const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
    const [showTaskTypeFilter, setShowTaskTypeFilter] = useState(false);
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();

    useEffect(() => {
        if (user?.userId) {
            loadTasks();
        }
    }, [user]);

    useEffect(() => {
        const handleTasksUpdated = () => {
            if (user?.userId) {
                loadTasks();
            }
        };

        window.addEventListener('tasks-updated', handleTasksUpdated);
        window.addEventListener('storage', handleTasksUpdated);

        return () => {
            window.removeEventListener('tasks-updated', handleTasksUpdated);
            window.removeEventListener('storage', handleTasksUpdated);
        };
    }, [user]);

    const formatAction = (action) => {
        if (!action) return '';
        if (action === 'Pending') return action;
        if (action.toLowerCase() === 'terminate') return 'Terminated';
        if (action.toLowerCase().endsWith('d') || action.toLowerCase().endsWith('ed')) return action;
        return `${action}d`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return dateString;
        }
    };

    const loadTasks = async () => {
        try {
            setError(null);
            const data = await TasksService.getMyTasks(user.userId);
            setTasks(data);
        } catch (error) {
            console.error('Error loading tasks:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleTaskClick = (taskId) => {
        // Close menu in mobile when clicking task
        if (window.innerWidth <= 768) {
            // This would need to be passed down if needed, but for now we'll skip it
            // setIsMenuCollapsed(false);
        }

        // Find the main panel
        const mainPanel = panels.find(p => p.code === 'Main');
        
        // If main panel exists and is hidden, show it
        if (mainPanel && !sectionVisibility[mainPanel.id] && onSectionVisibilityChange) {
            onSectionVisibilityChange(mainPanel.id, 'toggle');
        }

        // Navigate to the task
        navigate(`/user-dashboard/task/${taskId}`);
    };

    const getTimeLeft = (dueDate) => {
        if (!dueDate) return null;
        const now = new Date();
        const due = new Date(dueDate);
        const diffTime = due - now;
        const diffHours = diffTime / (1000 * 60 * 60);
        return diffHours;
    };

    const getProgressBarVariant = (timeLeft) => {
        if (timeLeft === null) return 'secondary';
        if (timeLeft < 0) return 'danger';
        if (timeLeft <= 2) return 'warning'; // Less than 2 hours
        if (timeLeft <= 4) return 'info'; // Less than 4 hours
        return 'success';
    };

    const getTimeLeftText = (timeLeft) => {
        if (timeLeft === null) return 'No time limit';
        
        if (timeLeft < 0) {
            const overdue = Math.abs(timeLeft);
            if (overdue < 1) {
                const minutes = Math.ceil(overdue * 60);
                return `${minutes} minutes overdue`;
            }
            if (overdue < 24) {
                return `${Math.ceil(overdue)} hours overdue`;
            }
            const days = Math.floor(overdue / 24);
            const hours = Math.ceil(overdue % 24);
            return hours === 0 
                ? `${days} days overdue`
                : `${days} days ${hours} hours overdue`;
        }
        
        if (timeLeft < 1) {
            const minutes = Math.ceil(timeLeft * 60);
            return `${minutes} minutes left`;
        }
        if (timeLeft < 24) {
            return `${Math.ceil(timeLeft)} hours left`;
        }
        const days = Math.floor(timeLeft / 24);
        const hours = Math.ceil(timeLeft % 24);
        return hours === 0 
            ? `${days} days left`
            : `${days} days ${hours} hours left`;
    };

    const getProgressPercentage = (timeLeft) => {
        if (timeLeft === null) return 0;
        if (timeLeft < 0) return 100;
        
        // Assuming a maximum of 7 days for the progress bar
        const maxHours = 168; // 7 days * 24 hours
        return Math.min(100, Math.max(0, ((maxHours - timeLeft) / maxHours) * 100));
    };

    const getStatusBadgeVariant = (status) => {
        switch (status) {
            case 'Completed':
                return 'success';
            case 'InProgress':
                return 'warning-light';
            case 'Rejected':
                return 'danger-light';
            default:
                return 'secondary';
        }
    };

    const getActionIcon = (action) => {
        if (!action) return null;
        const actionLower = action.toLowerCase();
        if (actionLower.includes('approve')) {
            return <FaCheckCircle className="text-success" size={14} title="Approved" />;
        } else if (actionLower.includes('reject')) {
            return <FaTimesCircle className="text-danger" size={14} title="Rejected" />;
        } else if (actionLower.includes('terminate')) {
            return <FaCheckCircle className="text-success" size={14} title="Terminated" />;
        } else if (actionLower.includes('cancel')) {
            return <FaCheckCircle className="text-success" size={14} title="Cancelled" />;
        }
        return null;
    };

    // Convert hex to RGB for background tinting (same as other components)
    const hexToRgb = (hex) => {
        if (!hex) return null;
        const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
        hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
        
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : null;
    };
    
    const getBackgroundColor = () => {
        // Access baseColor from userPreferences stored in CSS variables
        const baseColor = getComputedStyle(document.documentElement).getPropertyValue('--base-color').trim();
        const opacity = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--opacity') || '0.85');
        
        if (!baseColor) {
            // Use default themed background when no base color is set
            return null; // Let CSS handle the default
        }
        
        // Convert base color to RGB
        const rgb = hexToRgb(baseColor);
        if (!rgb) return null;
        
        if (darkMode) {
            // Dark mode: Use the actual base color with opacity
            return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity * 0.95})`;
        } else {
            // Light mode: Use a lighter tint of the base color
            const lightFactor = 0.35; // Keep 35% of the original color
            const lightR = Math.round(rgb.r * lightFactor + 255 * (1 - lightFactor));
            const lightG = Math.round(rgb.g * lightFactor + 255 * (1 - lightFactor));
            const lightB = Math.round(rgb.b * lightFactor + 255 * (1 - lightFactor));
            return `rgba(${lightR}, ${lightG}, ${lightB}, ${opacity * 0.95})`;
        }
    };

    // Filter and sort tasks
    const closedStatuses = ['Terminated', 'Cancelled', 'Rejected'];

    const filteredTasks = tasks
        .filter(task => {
            const matchesSearch = !searchTerm || 
                task.workflowName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                task.initiatedByUserName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                task.status?.toLowerCase().includes(searchTerm.toLowerCase());
            
            // Handle special cases for summary badge filters
            const matchesStatus = !statusFilter || 
                (statusFilter === 'Pending'
                    ? task.currentUserAction === 'Pending'
                    : statusFilter === 'Closed'
                        ? closedStatuses.includes(task.status)
                        : task.status === statusFilter);
            
            const matchesTaskType = !taskTypeFilter || task.workflowName === taskTypeFilter;
            
            return matchesSearch && matchesStatus && matchesTaskType;
        })
        .sort((a, b) => {
            let aValue, bValue;
            
            if (sortBy === 'updatedAt') {
                aValue = new Date(a.updatedAt);
                bValue = new Date(b.updatedAt);
            } else if (sortBy === 'dueDate') {
                aValue = a.dueDate ? new Date(a.dueDate) : new Date('9999-12-31'); // Put null dates at end
                bValue = b.dueDate ? new Date(b.dueDate) : new Date('9999-12-31');
            }
            
            if (sortOrder === 'asc') {
                return aValue - bValue;
            } else {
                return bValue - aValue;
            }
        });

    // Get unique values for filter options
    const uniqueStatuses = [...new Set(tasks.map(task => task.status))].filter(Boolean);
    const uniqueTaskTypes = [...new Set(tasks.map(task => task.workflowName))].filter(Boolean);

    // Count tasks by status and action
    const pendingTasksCount = tasks.filter(task => task.currentUserAction === "Pending").length;
    const completedTasksCount = tasks.filter(task => task.status === "Completed").length;
    const closedTasksCount = tasks.filter(task => closedStatuses.includes(task.status)).length;

    // Handle sort changes
    const handleSortChange = (newSortBy) => {
        if (sortBy === newSortBy) {
            // Toggle order if same sort field
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            // New sort field, default to desc
            setSortBy(newSortBy);
            setSortOrder('desc');
        }
    };

    const getSortIcon = (field) => {
        if (sortBy !== field) return <FaSort className="opacity-50" size={12} />;
        return sortOrder === 'asc' ? <FaSortUp size={12} /> : <FaSortDown size={12} />;
    };

    // Auto-close other popovers when one opens (simplified since we only have one filter now)
    const handleTaskTypeFilterToggle = (show) => {
        setShowTaskTypeFilter(show);
    };

    if (loading) {
        return <Spinner />;
    }

    return (
        <>
            {/* Task Summary Badges - Sticky/Frozen */}
            <div 
                className="p-2 mb-1"
                data-theme={darkMode ? 'dark' : 'light'}
                style={{
                    position: 'sticky',
                    top: '0px',
                    zIndex: 102,
                    background: getBackgroundColor() || (darkMode 
                        ? 'rgba(45, 45, 45, 0.98)' 
                        : 'rgba(255, 255, 255, 0.98)'
                    ),
                    backdropFilter: `blur(calc(var(--blur, 1) * 12px))`,
                    borderRadius: '6px',
                    border: darkMode 
                        ? '1px solid rgba(255, 255, 255, 0.12)' 
                        : '1px solid rgba(0, 0, 0, 0.12)',
                    marginBottom: '0px',
                    boxShadow: darkMode 
                        ? '0 2px 8px rgba(0, 0, 0, 0.2)' 
                        : '0 2px 8px rgba(0, 0, 0, 0.08)'
                }}
            >
                <div className="d-flex flex-wrap align-items-center gap-2" style={{ fontSize: '0.75rem' }}>
                    <div 
                        className="d-flex align-items-center gap-1 task-summary-badge" 
                        style={{ cursor: 'pointer' }}
                        onClick={() => setStatusFilter('')}
                        title="Show all tasks"
                    >
                        <Badge 
                            bg="primary"
                            style={{ 
                                padding: '3px 8px',
                                borderRadius: '10px',
                                fontWeight: '600',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {tasks.length}
                        </Badge>
                        <span className={`${darkMode ? 'text-light' : 'text-dark'}`} style={{ fontSize: '0.8rem' }}>
                            Total
                        </span>
                    </div>
                    
                    {pendingTasksCount > 0 && (
                        <div 
                            className="d-flex align-items-center gap-1 task-summary-badge" 
                            style={{ cursor: 'pointer' }}
                            onClick={() => setStatusFilter('Pending')}
                            title="Show pending tasks"
                        >
                            <Badge 
                                bg="warning"
                                style={{ 
                                    padding: '3px 8px',
                                    borderRadius: '10px',
                                    fontWeight: '600',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {pendingTasksCount}
                            </Badge>
                            <span className={`${darkMode ? 'text-light' : 'text-dark'}`} style={{ fontSize: '0.8rem' }}>
                                To Do
                            </span>
                        </div>
                    )}
                    
                    {completedTasksCount > 0 && (
                        <div 
                            className="d-flex align-items-center gap-1 task-summary-badge" 
                            style={{ cursor: 'pointer' }}
                            onClick={() => setStatusFilter('Completed')}
                            title="Show completed tasks"
                        >
                            <Badge 
                                bg="success"
                                style={{ 
                                    padding: '3px 8px',
                                    borderRadius: '10px',
                                    fontWeight: '600',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {completedTasksCount}
                            </Badge>
                            <span className={`${darkMode ? 'text-light' : 'text-dark'}`} style={{ fontSize: '0.8rem' }}>
                                Completed
                            </span>
                        </div>
                    )}
                    
                    {closedTasksCount > 0 && (
                        <div 
                            className="d-flex align-items-center gap-1 task-summary-badge" 
                            style={{ cursor: 'pointer' }}
                            onClick={() => setStatusFilter('Closed')}
                            title="Show closed tasks (Terminated, Cancelled, Rejected)"
                        >
                            <Badge 
                                bg="danger"
                                style={{ 
                                    padding: '3px 8px',
                                    borderRadius: '10px',
                                    fontWeight: '600',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {closedTasksCount}
                            </Badge>
                            <span className={`${darkMode ? 'text-light' : 'text-dark'}`} style={{ fontSize: '0.8rem' }}>
                                Closed
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Filter and Search Controls - Sticky/Frozen */}
            <div 
                className="p-1"
                data-theme={darkMode ? 'dark' : 'light'}
                style={{
                    position: 'sticky',
                    top: '36px', // Ultra close to summary badges
                    zIndex: 101,
                    background: getBackgroundColor() || (darkMode 
                        ? 'rgba(45, 45, 45, 0.85)' 
                        : 'rgba(255, 255, 255, 0.85)'
                    ),
                    backdropFilter: `blur(calc(var(--blur, 1) * 10px))`,
                    borderRadius: '6px',
                    border: darkMode 
                        ? '1px solid rgba(255, 255, 255, 0.10)' 
                        : '1px solid rgba(0, 0, 0, 0.10)',
                    marginBottom: '0px',
                    boxShadow: darkMode 
                        ? '0 2px 6px rgba(0, 0, 0, 0.15)' 
                        : '0 2px 6px rgba(0, 0, 0, 0.06)'
                }}
            >
                <div className="d-flex align-items-center gap-1">
                    {/* Search */}
                    <div className="flex-fill">
                        <InputGroup size="sm">
                            <InputGroup.Text 
                                className={`border-0 ${darkMode ? 'text-light' : 'text-dark'}`}
                                style={{ backgroundColor: 'transparent', padding: '2px 6px' }}
                            >
                                <FaSearch size={12} className="opacity-75" />
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder="Search tasks..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className={`border-0 ${darkMode ? 'text-light search-dark' : 'text-dark search-light'}`}
                                style={{ 
                                    fontSize: '0.8rem',
                                    backgroundColor: 'transparent',
                                    outline: 'none',
                                    boxShadow: 'none'
                                }}
                            />
                        </InputGroup>
                    </div>
                    
                    {/* Sort */}
                    <div className="btn-group" role="group">
                        <Button
                            variant={sortBy === 'updatedAt' ? 'primary' : 'outline-secondary'}
                            size="sm"
                            onClick={() => handleSortChange('updatedAt')}
                            style={{ fontSize: '0.7rem', padding: '1px 4px' }}
                            title="Sort by Date"
                        >
                            Date {getSortIcon('updatedAt')}
                        </Button>
                        <Button
                            variant={sortBy === 'dueDate' ? 'primary' : 'outline-secondary'}
                            size="sm"
                            onClick={() => handleSortChange('dueDate')}
                            style={{ fontSize: '0.7rem', padding: '1px 4px' }}
                            title="Sort by Due Date"
                        >
                            Due {getSortIcon('dueDate')}
                        </Button>
                    </div>
                    
                    {/* Task Type Filter */}
                    <OverlayTrigger
                        trigger="click"
                        placement="bottom-end"
                        show={showTaskTypeFilter}
                        onToggle={handleTaskTypeFilterToggle}
                        overlay={
                            <Popover 
                                id="tasktype-filter-popover"
                                style={{
                                    background: getBackgroundColor() || (darkMode 
                                        ? 'rgba(45, 45, 45, 0.98)' 
                                        : 'rgba(255, 255, 255, 0.98)'
                                    ),
                                    border: darkMode ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.15)',
                                    borderRadius: '8px',
                                    backdropFilter: 'blur(10px)',
                                    minWidth: '200px',
                                    color: darkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)'
                                }}
                            >
                                <Popover.Body className="p-2">
                                    <div className="d-grid gap-1" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                        <Button
                                            variant={!taskTypeFilter ? "primary" : (darkMode ? "secondary" : "light")}
                                            size="sm"
                                            className={!taskTypeFilter ? "" : (darkMode ? "text-light" : "text-dark")}
                                            onClick={() => {
                                                setTaskTypeFilter('');
                                                setShowTaskTypeFilter(false);
                                            }}
                                            style={{ fontSize: '0.8rem', padding: '4px 8px' }}
                                        >
                                            All Types
                                        </Button>
                                        {uniqueTaskTypes.map(taskType => (
                                            <Button
                                                key={taskType}
                                                variant={taskTypeFilter === taskType ? "primary" : (darkMode ? "secondary" : "light")}
                                                size="sm"
                                                className={taskTypeFilter === taskType ? "" : (darkMode ? "text-light" : "text-dark")}
                                                onClick={() => {
                                                    setTaskTypeFilter(taskType);
                                                    setShowTaskTypeFilter(false);
                                                }}
                                                style={{ 
                                                    fontSize: '0.8rem', 
                                                    padding: '4px 8px',
                                                    textAlign: 'left',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {taskType}
                                            </Button>
                                        ))}
                                    </div>
                                </Popover.Body>
                            </Popover>
                        }
                    >
                        <Button
                            variant="link"
                            size="sm"
                            className={`p-1 ${taskTypeFilter ? 'text-primary' : (darkMode ? 'text-light' : 'text-dark')}`}
                            title="Filter by Task Type"
                        >
                            <FaFilter size={12} />
                            {taskTypeFilter && <Badge bg="primary" className="ms-1" style={{ fontSize: '0.6rem' }}>1</Badge>}
                        </Button>
                    </OverlayTrigger>

                    {/* Clear */}
                    {(taskTypeFilter || searchTerm) && (
                        <Button
                            variant="link"
                            size="sm"
                            className={`p-1 ${darkMode ? 'text-light' : 'text-muted'}`}
                            onClick={() => {
                                setTaskTypeFilter('');
                                setSearchTerm('');
                            }}
                            title="Clear Search & Filters"
                        >
                            <FaTimes size={12} />
                        </Button>
                    )}
                </div>
                {(searchTerm || taskTypeFilter) && (
                    <div className="mt-1">
                        <small className={`${darkMode ? 'text-light' : 'text-muted'}`} style={{ fontSize: '0.7rem', opacity: 0.8 }}>
                            {filteredTasks.length} of {tasks.length} tasks
                            {searchTerm && ` • "${searchTerm}"`}
                            {taskTypeFilter && ` • ${taskTypeFilter}`}
                        </small>
                    </div>
                )}
            </div>

        <Container fluid className="p-2" style={{ height: '100%', overflow: 'hidden' }}>
            <style>
                {`
                    .bg-warning-light {
                        background-color: #ffe0b2 !important;
                        color: #b35900 !important;
                        border: 1px solid #ffb74d;
                    }
                    .bg-danger-light {
                        background-color: #ef5350 !important;
                        color: white !important;
                        border: 1px solid #e57373;
                    }
                    
                    /* Compact filter section */
                    .filter-search-section {
                        min-height: 32px;
                    }
                    
                    /* Search placeholder styling */
                    .search-light::placeholder {
                        color: rgba(0, 0, 0, 0.5) !important;
                    }
                    
                    .search-dark::placeholder {
                        color: rgba(255, 255, 255, 0.5) !important;
                    }
                    
                    /* Clickable badge hover effects */
                    .task-summary-badge:hover {
                        transform: translateY(-1px);
                        opacity: 0.9;
                    }
                    
                    /* Custom Scrollbar Styling */
                    .custom-scrollbar::-webkit-scrollbar {
                        width: 8px;
                    }
                    
                    .custom-scrollbar::-webkit-scrollbar-track {
                        background: ${darkMode 
                            ? 'rgba(255, 255, 255, 0.05)' 
                            : 'rgba(0, 0, 0, 0.05)'
                        };
                        border-radius: 10px;
                    }
                    
                    .custom-scrollbar::-webkit-scrollbar-thumb {
                        background: ${darkMode 
                            ? 'rgba(255, 255, 255, 0.15)' 
                            : 'rgba(0, 0, 0, 0.15)'
                        };
                        border-radius: 10px;
                        transition: background 0.3s ease;
                    }
                    
                    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                        background: ${darkMode 
                            ? 'rgba(255, 255, 255, 0.25)' 
                            : 'rgba(0, 0, 0, 0.25)'
                        };
                    }
                    
                    /* Firefox scrollbar */
                    .custom-scrollbar {
                        scrollbar-width: thin;
                        scrollbar-color: ${darkMode 
                            ? 'rgba(255, 255, 255, 0.15) rgba(255, 255, 255, 0.05)' 
                            : 'rgba(0, 0, 0, 0.15) rgba(0, 0, 0, 0.05)'
                        };
                    }
                `}
            </style>
            {error && (
                <div className="alert alert-danger py-1 px-2 mb-2 small" role="alert">
                    {error}
                </div>
            )}
            

            
            <div 
                className="task-list custom-scrollbar"
                style={{
                    maxHeight: 'calc(100vh - 180px)',
                    overflowY: 'auto',
                    paddingRight: '4px',
                    marginTop: '6px'
                }}
            >
                {filteredTasks.length === 0 ? (
                    <div className={`text-center py-4 ${darkMode ? 'text-light' : 'text-muted'}`}>
                        <div className="mb-2">
                            <FaFilter size={24} className="opacity-50" />
                        </div>
                        <p className="mb-1">No tasks found</p>
                        <small>
                            {tasks.length === 0 
                                ? "You don't have any tasks yet."
                                : "Try adjusting your search or filter criteria."
                            }
                        </small>
                    </div>
                ) : (
                    filteredTasks.map((task) => {
                    const shouldShowDueDate = task.currentUserAction === "Pending" && !['Completed', 'Rejected'].includes(task.status);
                    const pendingOlaDueDate = task.oLADueDate || task.olaDueDate || task.OLADueDate || task.dueDate;
                    const timeLeft = getTimeLeft(shouldShowDueDate ? pendingOlaDueDate : task.dueDate);
                    const progressVariant = shouldShowDueDate ? getProgressBarVariant(timeLeft) : 'secondary';
                    const progressPercentage = getProgressPercentage(timeLeft);
                    const timeLeftText = getTimeLeftText(timeLeft);

                    return (
                        <div 
                            key={task.id} 
                            className="task-item mb-2 p-2 rounded" 
                            onClick={() => handleTaskClick(task.id)}
                            style={{ 
                                cursor: 'pointer',
                                backgroundColor: 'rgba(0,0,0,0.02)',
                                transition: 'background-color 0.2s ease',
                                border: '1px solid rgba(0,0,0,0.05)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.02)';
                            }}
                        >
                            <div className="mb-1">
                                <div className="d-flex justify-content-between align-items-center gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <h6 className="mb-0 me-1 small">{task.workflowName}</h6>
                                        {getActionIcon(task.currentUserAction)}
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="text-muted" style={{ fontSize: '0.7rem', opacity: 0.7 }}>
                                            {formatDate(task.startedAt)}
                                    </div>
                                    {shouldShowDueDate && (
                                        <Badge bg={progressVariant} className="small">
                                            {timeLeftText}
                                        </Badge>
                                    )}
                                </div>
                                </div>
                                
                                {/* User Information */}
                                <div className="d-flex align-items-center gap-2 mt-1 mb-1">
                                    <ProfileImage
                                        imageUrl={task.initiatedByUserProfileImage}
                                        width={24}
                                        height={24}
                                        className="rounded-circle"
                                    />
                                    <div>
                                        <div className={`small fw-medium ${darkMode ? 'text-light' : 'text-dark'}`} style={{ fontSize: '0.75rem' }}>
                                            {task.initiatedByUserName}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <ProgressBar 
                                variant={progressVariant}
                                now={progressPercentage}
                                className="mb-1"
                                style={{ 
                                    height: '8px',
                                    opacity: shouldShowDueDate ? '1' : '0.5'
                                }}
                            />
                            
                            <div className="task-details d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center gap-2">
                                    <Badge bg={getStatusBadgeVariant(task.status)} className="small">
                                        {task.status}
                                    </Badge>
                                    {!shouldShowDueDate && (
                                        <Badge bg="info" className="small">
                                            {formatAction(task.currentUserAction)}
                                            {task.currentUserActionDate && (
                                                <span className="ms-1 opacity-75">
                                                    ({formatDate(task.currentUserActionDate)})
                                                </span>
                                            )}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                    })
                )}
            </div>
        </Container>
        </>
    );
};

export default TaskList; 